/**
 * Canonical formatting utils for public event cards.
 * Single source of truth for timing badge, time row, and season text.
 */
import { format, isToday, isTomorrow } from "date-fns";
import { sv } from "date-fns/locale";

export type TimingMode = "date_time" | "open" | "anytime" | "season";

function formatShortDate(date: Date): string {
  return format(date, "d MMM", { locale: sv });
}

function formatLongDate(date: Date): string {
  return format(date, "d MMMM", { locale: sv });
}

function formatOpenWindowLabel(startDate?: string | null, endDate?: string | null): string {
  // Both dates set: explicit range "Öppet [start]–[end]"
  if (startDate && endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
      return `Öppet ${formatShortDate(start)}–${formatShortDate(end)}`;
    }
  }

  // Only start date set (end is null): open from date indefinitely "Öppet från [start]"
  if (startDate && !endDate) {
    const start = new Date(startDate);
    if (!Number.isNaN(start.getTime())) {
      return `Öppet från ${formatShortDate(start)}`;
    }
  }

  // No dates at all (both null): open season indefinitely "Öppet under säsong"
  return "Öppet under säsong";
}

/**
 * Badge text shown over the event image (top-left).
 * NEVER includes clock time.
 */
export function formatTimingBadge(
  timingMode: TimingMode,
  startDate: string,
  outingTags?: string[],
  endDate?: string | null,
): string {
  if (timingMode === "open" || timingMode === "anytime") {
    return formatOpenWindowLabel(startDate || null, endDate ?? null);
  }
  if (timingMode === "season") {
    const seasons = (outingTags || [])
      .filter((t) => t.startsWith("season:"))
      .map((t) => {
        const s = t.replace("season:", "");
        return s.charAt(0).toUpperCase() + s.slice(1);
      });
    if (seasons.length === 0) return "Bäst under säsong";
    const MAX = 2;
    const visible = seasons.slice(0, MAX);
    const rest = seasons.length - MAX;
    const seasonLabel = rest > 0
      ? `${visible.join(" · ")} +${rest}`
      : visible.join(" · ");
    return `Bäst under säsong – ${seasonLabel}`;
  }
  // date_time
  const d = new Date(startDate);
  if (isToday(d)) return "Idag";
  if (isTomorrow(d)) return "Imorgon";
  return format(d, "d MMM", { locale: sv });
}

/**
 * Time row text shown below title. Only for date_time.
 * Returns null for anytime/season.
 */
export function formatTimeRow(
  timingMode: TimingMode,
  startDate: string,
  endDate?: string | null,
): string | null {
  if (timingMode !== "date_time") return null;
  const start = new Date(startDate);
  const startTime = format(start, "HH:mm", { locale: sv });
  if (!endDate) return `kl ${startTime}`;
  const end = new Date(endDate);
  const endTime = format(end, "HH:mm", { locale: sv });
  if (endTime <= startTime) return `kl ${startTime}`;
  return `kl ${startTime}–${endTime}`;
}

/**
 * Compact season text from outing_tags (season:xxx).
 * Max 3 tokens, otherwise "Flera säsonger".
 */
export function formatSeasonText(outingTags: string[]): string | null {
  const seasons = outingTags
    .filter((t) => t.startsWith("season:"))
    .map((t) => {
      const s = t.replace("season:", "");
      return s.charAt(0).toUpperCase() + s.slice(1);
    });
  if (seasons.length === 0) return null;
  if (seasons.length > 3) return "Flera säsonger";
  return seasons.join(" · ");
}

/**
 * Canonical human-readable timing line across cards, detail and previews.
 */
export function formatEventTimingSummary(
  timingMode: TimingMode,
  startDate?: string | null,
  endDate?: string | null,
  outingTags?: string[],
): string {
  if (timingMode === "open" || timingMode === "anytime") {
    return formatOpenWindowLabel(startDate ?? null, endDate ?? null);
  }

  if (timingMode === "season") {
    const seasonText = formatSeasonText(outingTags || []);
    return seasonText ? `Bäst under ${seasonText.toLowerCase()}` : "Bäst under säsong";
  }

  if (!startDate) return "Datum saknas";

  const start = new Date(startDate);
  if (Number.isNaN(start.getTime())) return "Datum saknas";

  const dateLabel = format(start, "EEEE d MMMM yyyy", { locale: sv });
  const timeRow = formatTimeRow("date_time", startDate, endDate);
  if (!timeRow) return dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1);
  return `${dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1)}, ${timeRow}`;
}

/**
 * Place label for event card.
 * If a custom location string is provided, use it directly.
 * Otherwise fall back to "Hos {producerName}".
 */
export function formatPlaceLabel(
  producerName: string | null,
  producerCity?: string | null,
  location?: string | null,
): string | null {
  if (location) return location;
  if (!producerName) return null;
  return `Hos ${producerName}${producerCity ? `, ${producerCity}` : ""}`;
}
